import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlignCenter,
  AlignLeft,
  ArrowLeft,
  Bold,
  Italic,
  PenLine,
} from "lucide-react";

import {
  LETTER_FONTS,
  LETTER_TEXT_STYLES,
} from "../../utils/letterPreferences";
import {
  execEditorCommand,
  getEditorSelectionState,
  materializePendingTypingStyle,
} from "../../utils/joditHelpers";

/** Keys that move the caret / change context — safe to re-read format state. */
const SELECTION_SYNC_KEYS = new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  "PageUp",
  "PageDown",
  "Enter",
  "Backspace",
  "Delete",
]);

function ToolbarIconButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      // Keep editor selection when clicking toolbar (prevents focus steal).
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`flex flex-1 items-center justify-center rounded-md py-2.5 transition duration-200 ${
        active
          ? "bg-primaryText/15 text-primaryText"
          : "text-secondaryText hover:bg-black/[0.04]"
      }`}
    >
      {children}
    </button>
  );
}

function StyleOptionButton({ active, onClick, children, className = "" }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-xl border bg-white px-4 py-3 text-left transition duration-200 ${
        active
          ? "border-primaryText text-primaryText"
          : "border-transparent text-secondaryText hover:border-greyLight/80"
      } ${className}`}
    >
      {children}
    </button>
  );
}

const TEXT_STYLE_LABEL_CLASS = {
  title: "font-logo text-lg leading-tight",
  subtitle: "font-josefin text-base font-medium",
  text: "font-josefin text-sm",
};

function LetterEditorSidebar({
  editorInstanceRef,
  preferences,
  onPreferenceChange,
  editorReady,
  readOnly = false,
}) {
  const navigate = useNavigate();
  const [activeFormat, setActiveFormat] = useState({
    bold: false,
    italic: false,
    left: false,
    center: true,
  });
  const [selectionPrefs, setSelectionPrefs] = useState({
    fontFamily: preferences.fontFamily,
    textStyle: preferences.textStyle,
  });
  const suppressSyncUntilRef = useRef(0);
  const rafSyncRef = useRef(0);
  const activeFormatRef = useRef(activeFormat);

  useEffect(() => {
    activeFormatRef.current = activeFormat;
  }, [activeFormat]);

  // Seed panel from letter defaults only — caret sync owns live highlighting.
  // Do not keep overwriting from preferences after every font pick (that fought
  // the focused selection when applyToWorkspace lagged the caret font).
  useEffect(() => {
    setSelectionPrefs({
      fontFamily: preferences.fontFamily,
      textStyle: preferences.textStyle,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed when letter prefs identity changes on load
  }, [preferences.fontFamily]);

  const applySelectionState = useCallback((state) => {
    const nextFormat = {
      bold: state.bold,
      italic: state.italic,
      left: state.left,
      center: state.center,
    };
    const prev = activeFormatRef.current;
    if (
      prev.bold !== nextFormat.bold ||
      prev.italic !== nextFormat.italic ||
      prev.left !== nextFormat.left ||
      prev.center !== nextFormat.center
    ) {
      setActiveFormat(nextFormat);
    }

    setSelectionPrefs((prevPrefs) => {
      // getEditorSelectionState already returns a canonical LETTER_FONTS id.
      const fontFamily = state.fontFamily || prevPrefs.fontFamily;
      const textStyle = state.textStyle || prevPrefs.textStyle;
      if (
        fontFamily === prevPrefs.fontFamily &&
        textStyle === prevPrefs.textStyle
      ) {
        return prevPrefs;
      }
      return { fontFamily, textStyle };
    });
  }, []);

  const syncFromSelection = useCallback(() => {
    if (Date.now() < suppressSyncUntilRef.current) return;
    if (!editorInstanceRef.current) return;
    // getEditorSelectionState overlays pending typing style so a click next
    // to a neighbor word cannot wipe the toolbar highlight the user set.
    applySelectionState(getEditorSelectionState(editorInstanceRef.current));
  }, [applySelectionState, editorInstanceRef]);

  const scheduleSyncFromSelection = useCallback(() => {
    if (Date.now() < suppressSyncUntilRef.current) return;
    if (rafSyncRef.current) cancelAnimationFrame(rafSyncRef.current);
    rafSyncRef.current = requestAnimationFrame(() => {
      rafSyncRef.current = 0;
      syncFromSelection();
    });
  }, [syncFromSelection]);

  useEffect(() => {
    if (!editorReady || !editorInstanceRef.current) return undefined;

    const editor = editorInstanceRef.current;
    const el = editor.editor;

    const onKeyUp = (event) => {
      // Ignore plain character typing — format is sticky and queryCommandState
      // flickers mid-insert when typing fast, which was flipping B/I off/on.
      if (SELECTION_SYNC_KEYS.has(event.key)) {
        scheduleSyncFromSelection();
      }
    };

    el?.addEventListener("mouseup", scheduleSyncFromSelection);
    el?.addEventListener("keyup", onKeyUp);
    // Do NOT sync on Jodit's changeSelection — it fires on every keystroke
    // and queryCommandState flickers mid-insert when typing fast.
    syncFromSelection();

    return () => {
      el?.removeEventListener("mouseup", scheduleSyncFromSelection);
      el?.removeEventListener("keyup", onKeyUp);
      if (rafSyncRef.current) cancelAnimationFrame(rafSyncRef.current);
    };
  }, [
    editorReady,
    editorInstanceRef,
    scheduleSyncFromSelection,
    syncFromSelection,
  ]);

  const exec = (command, value) => {
    if (!editorReady || readOnly) return false;
    return execEditorCommand(editorInstanceRef.current, command, value);
  };

  const toggleFormat = (command) => {
    if (readOnly) return;
    // Give the command a short quiet window while Jodit settles its selection;
    // the immediate highlight below still comes from the real editor state.
    suppressSyncUntilRef.current = Date.now() + 400;
    const applied = exec(command);
    if (applied) {
      applySelectionState(getEditorSelectionState(editorInstanceRef.current));
    }

    window.setTimeout(() => {
      suppressSyncUntilRef.current = 0;
      syncFromSelection();
    }, 420);
  };

  const applyFont = (fontId) => {
    if (readOnly) return;
    suppressSyncUntilRef.current = Date.now() + 400;
    const applied = exec("fontname", fontId);
    if (!applied) return;
    applySelectionState(getEditorSelectionState(editorInstanceRef.current));
    // Keep workspace default in sync so unstyled text matches the panel.
    onPreferenceChange({ fontFamily: fontId }, { applyToWorkspace: true });
    // Rematerialize after workspace font updates so the caret leaves any
    // previous sticky font span (e.g. Poppins → Times New Roman).
    materializePendingTypingStyle(editorInstanceRef.current);
    window.setTimeout(() => {
      suppressSyncUntilRef.current = 0;
      syncFromSelection();
    }, 420);
  };

  const applyTextStyle = (style) => {
    if (readOnly) return;
    // Selection/block only — never persist textStyle as a letter-wide default
    // (that would cascade on reopen via the editor root font-size).
    if (exec("fontsize", style.size)) {
      applySelectionState(getEditorSelectionState(editorInstanceRef.current));
    }
  };

  return (
    <aside className="hidden h-screen w-[240px] shrink-0 flex-col overflow-y-auto bg-[#F7F3F2] px-4 py-5 sm:px-5 sm:py-6 lg:static lg:flex xl:w-[260px]">
      <div className="mb-6 flex items-center justify-between lg:mb-8">
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 px-1 font-josefin text-[11px] font-bold uppercase tracking-wider text-secondaryText transition hover:text-primaryText"
        >
          <ArrowLeft size={14} />
          Retour à mes lettres
        </button>
      </div>

      {readOnly && (
        <p className="mb-6 rounded-md bg-white/80 px-3 py-2.5 font-josefin text-xs leading-relaxed text-textMuted">
          Cette lettre a été envoyée. Tu peux la consulter, mais plus la
          modifier.
        </p>
      )}

      <section className={`mb-8 ${readOnly ? "pointer-events-none opacity-50" : ""}`}>
        <h2 className="mb-3 px-1 font-josefin text-xs font-bold uppercase tracking-wider text-secondaryText">
          Apparence
        </h2>
        <div className="flex rounded-xl bg-white p-1 shadow-sm">
          <ToolbarIconButton
            active={activeFormat.bold}
            onClick={() => toggleFormat("bold")}
          >
            <Bold size={16} strokeWidth={2.5} />
          </ToolbarIconButton>
          <ToolbarIconButton
            active={activeFormat.italic}
            onClick={() => toggleFormat("italic")}
          >
            <Italic size={16} />
          </ToolbarIconButton>
          <ToolbarIconButton
            active={activeFormat.left}
            onClick={() => {
              suppressSyncUntilRef.current = Date.now() + 400;
              if (exec("justifyLeft")) {
                applySelectionState(getEditorSelectionState(editorInstanceRef.current));
              }
              window.setTimeout(() => {
                suppressSyncUntilRef.current = 0;
                syncFromSelection();
              }, 420);
            }}
          >
            <AlignLeft size={16} />
          </ToolbarIconButton>
          <ToolbarIconButton
            active={activeFormat.center}
            onClick={() => {
              suppressSyncUntilRef.current = Date.now() + 400;
              if (exec("justifyCenter")) {
                applySelectionState(getEditorSelectionState(editorInstanceRef.current));
              }
              window.setTimeout(() => {
                suppressSyncUntilRef.current = 0;
                syncFromSelection();
              }, 420);
            }}
          >
            <AlignCenter size={16} />
          </ToolbarIconButton>
        </div>
      </section>

      <section className={`mb-8 ${readOnly ? "pointer-events-none opacity-50" : ""}`}>
        <h2 className="mb-3 px-1 font-josefin text-xs font-bold uppercase tracking-wider text-secondaryText">
          Styles de texte
        </h2>
        <div className="flex flex-col gap-2">
          {LETTER_TEXT_STYLES.map((style) => (
            <StyleOptionButton
              key={style.id}
              active={selectionPrefs.textStyle === style.id}
              onClick={() => applyTextStyle(style)}
            >
              <PenLine size={16} className="shrink-0 text-secondaryText/70" />
              <span className={TEXT_STYLE_LABEL_CLASS[style.id]}>
                {style.label}
              </span>
            </StyleOptionButton>
          ))}
        </div>
      </section>

      <section className={readOnly ? "pointer-events-none opacity-50" : ""}>
        <h2 className="mb-3 px-1 font-josefin text-xs font-bold uppercase tracking-wider text-secondaryText">
          Police
        </h2>
        <div className="flex flex-col gap-2">
          {LETTER_FONTS.map((font) => (
            <StyleOptionButton
              key={font.id}
              active={selectionPrefs.fontFamily === font.id}
              onClick={() => applyFont(font.id)}
            >
              <span className="text-sm leading-snug" style={{ fontFamily: font.id }}>
                {font.label}
              </span>
            </StyleOptionButton>
          ))}
        </div>
      </section>
    </aside>
  );
}

export default LetterEditorSidebar;
